import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { X, ZoomIn, ZoomOut, ChevronLeft, ChevronRight } from "lucide-react";

const Ctx = createContext(() => {});
export const useLightbox = () => useContext(Ctx);

/** Store-side full-screen image viewer with zoom. open(images[], index) */
export function LightboxProvider({ children }) {
  const [state, setState] = useState(null); // {images, i}
  const [zoom, setZoom] = useState(1);
  const open = useCallback((images, i = 0) => { const list = (images || []).filter(Boolean); if (list.length) { setState({ images: list, i }); setZoom(1); } }, []);
  const close = () => setState(null);
  useEffect(() => {
    if (!state) return;
    const onKey = (e) => { if (e.key === "Escape") close(); if (e.key === "ArrowRight") go(1); if (e.key === "ArrowLeft") go(-1); };
    window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey);
  });
  const go = (d) => setState((s) => s && { ...s, i: (s.i + d + s.images.length) % s.images.length });
  const bump = (d) => setZoom((z) => Math.min(4, Math.max(1, +(z + d).toFixed(2))));
  return (
    <Ctx.Provider value={open}>
      {children}
      {state && (
        <div data-testid="lightbox" className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90" onClick={close} onWheel={(e) => bump(e.deltaY < 0 ? 0.25 : -0.25)}>
          <div className="absolute right-3 top-3 flex gap-2" onClick={(e) => e.stopPropagation()}>
            <button data-testid="lightbox-zoom-out" onClick={() => bump(-0.5)} className="rounded-full bg-white/15 p-2 text-white hover:bg-white/25"><ZoomOut className="h-5 w-5" /></button>
            <span className="rounded-full bg-white/15 px-3 py-2 text-xs font-bold text-white">{Math.round(zoom * 100)}%</span>
            <button data-testid="lightbox-zoom-in" onClick={() => bump(0.5)} className="rounded-full bg-white/15 p-2 text-white hover:bg-white/25"><ZoomIn className="h-5 w-5" /></button>
            <button data-testid="lightbox-close" onClick={close} className="rounded-full bg-[#D46117] p-2 text-white"><X className="h-5 w-5" /></button>
          </div>
          {state.images.length > 1 && (<>
            <button data-testid="lightbox-prev" onClick={(e) => { e.stopPropagation(); go(-1); }} className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/15 p-2 text-white"><ChevronLeft className="h-6 w-6" /></button>
            <button data-testid="lightbox-next" onClick={(e) => { e.stopPropagation(); go(1); }} className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/15 p-2 text-white"><ChevronRight className="h-6 w-6" /></button>
          </>)}
          <div className="flex h-full w-full items-center justify-center overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
            <img data-testid="lightbox-image" src={state.images[state.i]} alt="" onClick={() => bump(zoom >= 2 ? -1 : 1)} style={{ transform: `scale(${zoom})`, transformOrigin: "center", transition: "transform 150ms" }} className={`max-h-[85vh] max-w-full object-contain ${zoom > 1 ? "cursor-zoom-out" : "cursor-zoom-in"}`} />
          </div>
          {state.images.length > 1 && <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-xs font-bold text-white/80">{state.i + 1} / {state.images.length}</div>}
        </div>
      )}
    </Ctx.Provider>
  );
}
